import {
  type BridgeData,
  bridgeDataReady,
  currentBridgeData,
} from '@databases/database-state/state/bridge-data';
import { snapshot, subscribe } from 'valtio';

import { type FrameToken } from '@canvas/frame-api-types';
import { transformToken } from '@canvas/frame-api/canvas-frame-api';
import { waitForContentAPI } from '@canvas/frame-api/host-api/host-frame-context';
import { getPaywallCacheFeatureData, updatePaywallFeatureState } from '@canvas/paywall';
import { captureException } from '@canvas/sentry-react';

import {
  getLastKnownDatabasePaywallFeatureState,
  syncDatabasePaywallFeatureState,
} from './database-paywall-feature-state-cache.ts';

const syncPaywallFeatureState = (token: FrameToken | undefined): boolean => {
  if (token?.features) {
    syncDatabasePaywallFeatureState(token.features);
    return true;
  }

  return false;
};

export const initializeDatabasePaywallFeatures = (): VoidFunction => {
  let hasSyncedPaywallFeatureData = false;
  let hasAppliedLastKnownPaywallFeatureData = false;
  let hasResolvedBridgeData = false;
  let hasResolvedCachedFeatureData = false;
  let lastSyncedBridgeFeaturesKey: string | undefined;

  const resolveWithLastKnownPaywallFeatureStateIfNeeded = () => {
    if (
      hasSyncedPaywallFeatureData ||
      hasAppliedLastKnownPaywallFeatureData ||
      !hasResolvedBridgeData
    ) {
      return;
    }

    const lastKnownPaywallFeatureState = getLastKnownDatabasePaywallFeatureState();

    if (!lastKnownPaywallFeatureState) {
      return;
    }

    hasAppliedLastKnownPaywallFeatureData = true;
    updatePaywallFeatureState(lastKnownPaywallFeatureState);
  };

  const resolveWithDefaultPaywallFeatureStateIfNeeded = () => {
    if (
      hasSyncedPaywallFeatureData ||
      hasAppliedLastKnownPaywallFeatureData ||
      !hasResolvedBridgeData ||
      !hasResolvedCachedFeatureData
    ) {
      return;
    }

    // Only fall back once the startup sources that may have paywall data have
    // resolved. If they are all missing features and there is no last-known
    // value for this tab, treat the user as locked via the empty default state.
    // A later bridge payload with features still overrides this because
    // updatePaywallFeatureState merges per feature.
    updatePaywallFeatureState({});
  };

  const sync = (token: FrameToken | undefined): boolean => {
    const didSync = syncPaywallFeatureState(token);

    if (didSync) {
      hasSyncedPaywallFeatureData = true;
    }

    return didSync;
  };

  const syncFromBridgeData = (bridgeData: BridgeData): boolean => {
    // bridge-data.ts already validates every payload when it updates the frame
    // context, so skip the schema validation (and its failure reporting) for
    // payloads that cannot carry entitlement anyway.
    if (!bridgeData.token?.features) {
      return false;
    }

    const token = transformToken(bridgeData.token);

    if (!token?.features) {
      return false;
    }

    const bridgeFeaturesKey = JSON.stringify(token.features);

    if (bridgeFeaturesKey === lastSyncedBridgeFeaturesKey) {
      return false;
    }

    lastSyncedBridgeFeaturesKey = bridgeFeaturesKey;

    return sync(token);
  };

  // bridgeDataReady resolves with the live Valtio proxy, and IndexedDB's
  // structured clone rejects proxies, so always read a plain snapshot before
  // the features reach the paywall cache.
  const syncFromCurrentBridgeData = () =>
    syncFromBridgeData(snapshot(currentBridgeData).data);

  // Confluence writes the iframe's bridge data before the token service has
  // delivered the token, then rewrites it a few seconds later with the token
  // attached. bridgeDataReady only observes the first write, so later payloads
  // have to be picked up from the Valtio store for the lifetime of the app shell.
  const unsubscribeFromBridgeDataUpdates = subscribe(
    currentBridgeData,
    syncFromCurrentBridgeData,
  );

  const bridgePaywallFeatureStatePromise = bridgeDataReady
    .get()
    .then(syncFromCurrentBridgeData)
    .finally(() => {
      hasResolvedBridgeData = true;
      resolveWithLastKnownPaywallFeatureStateIfNeeded();
      resolveWithDefaultPaywallFeatureStateIfNeeded();
    });
  void bridgePaywallFeatureStatePromise;

  getPaywallCacheFeatureData()
    .then((data) => {
      if (hasSyncedPaywallFeatureData || data === undefined) {
        return;
      }

      hasSyncedPaywallFeatureData = true;
      // This data already came from the shared cache, so avoid writing it back.
      syncDatabasePaywallFeatureState(data, { shouldWritePaywallCache: false });
    })
    .catch((error) => {
      captureException(error, {
        tags: {
          captureLocation:
            'initialize-database-paywall-features-get-paywall-cache-feature-data',
        },
      });
    })
    .finally(() => {
      hasResolvedCachedFeatureData = true;
      resolveWithDefaultPaywallFeatureStateIfNeeded();
    });

  waitForContentAPI().then(async (contentAPI) => {
    contentAPI.onTokenInit(sync);
    contentAPI.onTokenChange(sync);

    try {
      const didSyncFromContentAPI = sync(await contentAPI.getToken());

      if (!didSyncFromContentAPI) {
        await bridgePaywallFeatureStatePromise;
      }
    } catch {
      await bridgePaywallFeatureStatePromise;
    }
  });

  return unsubscribeFromBridgeDataUpdates;
};
